import { DEFAULT_THEME_SETTINGS } from '../renderer/context.js';
/**
 * Reject a merchant-controlled free string (font family / gradient) that carries CSS-declaration-breaking
 * or injection characters before it is serialized into the `;`-joined `<html style>` (0019 findings
 * #2/#14/#18 — the FRONT's escape layer, in addition to the backend allowlist/grammar VO). A hostile
 * value falls back to a safe default rather than emitting `;`/`}`/`<`/`url(`/`/*`/backslash/newline.
 */
function cleanCss(v, fallback) {
    if (typeof v !== 'string' || v.length === 0)
        return fallback;
    if (/[;{}<>\\]|url\(|\/\*|[\r\n]/i.test(v))
        return fallback;
    return v;
}
// Poppins alone, matching the seeded `StoreThemeSettings.font` and every theme preset — this is the
// no-theme fallback, used only when a shop has stored nothing at all. Manrope is NOT named here on
// purpose: it belongs in `SCRIPT_FALLBACKS` below, which is appended to whatever the shop actually
// stored and therefore reaches every shop rather than only the ones with an empty setting.
const POPPINS_STACK = "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
/**
 * A shop whose language is not written in the Latin alphabet needs a face that HAS its letters — and the
 * seller's chosen family almost certainly does not. Poppins ships no Arabic glyphs at all, so koffti (a
 * live `ar` shop) declared `'Poppins', system-ui, …` and its buyers read the whole shop in whatever the
 * operating system happened to substitute: Geeza Pro on a Mac, Tahoma on Windows, something else on
 * Android. The shop's typography simply did not apply to its own language.
 *
 * They are APPENDED, never prepended: on a page mixing scripts — an Arabic shop with a Latin brand name —
 * the seller's font still renders the Latin, and the browser only reaches these for the glyphs the first
 * family lacks. That per-glyph fallback is what a font stack is for.
 *
 * ## Appending only works for a script the GENERIC families do not already cover
 *
 * A stack ends in `sans-serif`, and a generic family always resolves to a concrete font — Helvetica on a
 * Mac. The browser walks the stack per glyph and stops at the first family that HAS the character, so
 * anything appended after `sans-serif` is only ever reached for glyphs Helvetica lacks. Arabic, Hebrew
 * and the CJK scripts qualify, which is why this table works for them. Cyrillic and Greek do NOT —
 * Helvetica writes both — so a Cyrillic family appended here would never be reached, and the shop would
 * keep rendering in the system face while the emitted CSS looked correct. Measured on preprod
 * 2026-08-20: appended, Cyrillic drew in `.SF NS`; inserted ahead of the generics, it drew in Manrope.
 *
 * That is why the hosted faces live in [HOSTED_SCRIPT_FACES] below and are INSERTED, not appended.
 *
 * The Arabic rows are still system-only. `buildFontFaces` (feature 0021) generates real `@font-face`
 * rules with an `arabic` subset and has NO callers, so no shop has ever emitted one — wiring it needs the
 * backend to mirror and serve the faces. Until then an Arabic shop is rendered by the operating system,
 * which is the gap Cyrillic and Greek have just left.
 */
const SCRIPT_FALLBACKS = {
    ar: "'Noto Naskh Arabic', 'Geeza Pro', 'Segoe UI', Tahoma",
    fa: "'Noto Naskh Arabic', 'Geeza Pro', 'Segoe UI', Tahoma",
    ur: "'Noto Nastaliq Urdu', 'Noto Naskh Arabic', 'Geeza Pro', Tahoma",
    he: "'Noto Sans Hebrew', 'Arial Hebrew', 'Segoe UI', Tahoma",
    hi: "'Noto Sans Devanagari', 'Kohinoor Devanagari', 'Nirmala UI'",
    th: "'Noto Sans Thai', 'Thonburi', 'Leelawadee UI'",
    zh: "'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei'",
    ja: "'Noto Sans JP', 'Hiragino Sans', 'Yu Gothic'",
    ko: "'Noto Sans KR', 'Apple SD Gothic Neo', 'Malgun Gothic'",
};
/**
 * The hosted faces the THEME ships for a script, by locale — inserted directly after the seller's own
 * family rather than appended, for the reason spelt out above: appended, they sit behind `sans-serif` and
 * are never reached for a script Helvetica can already write.
 *
 * Manrope is that face for Cyrillic and Greek (three subset files with a `unicode-range`, see
 * `@aquilixshop/tokens`). It reached no shop between shipping on 19/08 and this being wired on 20/08, and
 * the reason is worth writing down: a stored value always beats a default, and EVERY shop stores a font.
 * The seeded `StoreThemeSettings.font` names Poppins alone, and so does each theme preset a seller can
 * pick — eleven stacks across this package, of which exactly one ever named Manrope, and that one is the
 * no-theme fallback no real shop takes. So a Russian shop rendered its whole catalogue in whatever the
 * visitor's operating system supplied.
 *
 * Inserting HERE fixes every shop at once — existing rows included, whatever font the seller chose and
 * whatever theme they applied — because this runs on the stored stack rather than instead of it. No data
 * migration, and no way for a new theme preset to undo it.
 *
 * `Noto Sans` follows Manrope as the net: if the webfont fails to load, the shop still reads in something
 * chosen rather than in whatever the platform defaults to.
 *
 * Every locale the catalogue offers in one of these scripts must appear here. `bg` and `sr` shipped on
 * 19/08 with no entry anywhere, which is what a hand-kept list does; the suite now walks the dictionaries
 * instead, so the next Cyrillic language fails the moment it lands.
 */
const HOSTED_SCRIPT_FACES = {
    ru: "'Manrope', 'Noto Sans'",
    uk: "'Manrope', 'Noto Sans'",
    be: "'Manrope', 'Noto Sans'",
    bg: "'Manrope', 'Noto Sans'",
    mk: "'Manrope', 'Noto Sans'",
    sr: "'Manrope', 'Noto Sans'",
    el: "'Manrope', 'Noto Sans'",
};
/**
 * Insert the theme's own face for [locale]'s script immediately after the FIRST family of the stack, once.
 * The seller's family still leads, so a Latin brand name in a Russian shop stays in their font; the
 * theme's face comes next, ahead of `system-ui` and `sans-serif`, which is the only position the browser
 * will ever reach for a script the generics can write.
 */
export function withScriptFace(stack, locale) {
    const base = (locale ?? '').trim().toLowerCase().split(/[-_]/)[0] ?? '';
    const face = HOSTED_SCRIPT_FACES[base];
    if (!face || stack.includes(face))
        return stack;
    const cut = stack.indexOf(',');
    return cut === -1
        ? `${stack}, ${face}`
        : `${stack.slice(0, cut)}, ${face},${stack.slice(cut + 1)}`;
}
/**
 * Append the script fallback for [locale] to a font stack, once. Returns the stack untouched for a Latin
 * language, an unknown tag, or a stack that already names the fallback — this runs on every render and
 * must not grow the string each time.
 */
export function withScriptFallback(stack, locale) {
    const base = (locale ?? '').trim().toLowerCase().split(/[-_]/)[0] ?? '';
    const extra = SCRIPT_FALLBACKS[base];
    if (!extra || stack.includes(extra))
        return stack;
    return `${stack}, ${extra}`;
}
/**
 * Readable ink to lay ON an accent-2 fill. Unlike accent-1 — whose companion `solidButtonLabel` the seller
 * owns explicitly — accent-2 has no paired label field, yet sections paint bands with it (multirow /
 * vertical-ticker / comparison-table / slideshow-hero). A PALE accent-2 (the Atlas default `#d2ebff`) under
 * the accent band's white `solidButtonLabel` is unreadable, so this picks the ink by perceived luminance:
 * light fill -> the theme's text color, dark fill -> the seller's solid-button label. Cheap sRGB luma
 * (ITU-R BT.601) is enough for a light/dark verdict; a non-hex value falls back to the label.
 */
function accent2Ink(accent2, solidButtonLabel, text) {
    const digits = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(accent2.trim())?.[1];
    if (!digits)
        return solidButtonLabel;
    const h = digits.length === 3 ? digits.replace(/./g, (c) => c + c) : digits;
    const n = parseInt(h, 16);
    const luma = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
    return luma > 150 ? text : solidButtonLabel;
}
/** Shrine letter-spacing transform (Shrine "0.6" -> 0.06em); unit is `em` uniformly (0019 finding #11). */
const trackingEm = (role) => `${role.letterSpacing / 10}em`;
/**
 * Map the store's themeSettings (v2 Shrine model) onto the `--aqx-theme-*` design-token vars the
 * storefront sections actually read, so a single re-theme reflows every section across all three
 * `[data-scheme]` bands (feature 0019 §4b). Derived tokens (hover/muted/subtle/surface-2/border) are
 * `color-mix` of the emitted base vars, so they track a re-theme automatically. A subset of the legacy
 * builder-chrome `--aqx-*` vars are also emitted (FallbackSection/ErrorBoundaries/EditableSection still
 * read them). The old dead `--aqx-type-scale` is gone; `--aqx-theme-heading-scale`/`-body-scale` replace
 * it with REAL consumers (the `calc()`-rebased text tokens, §3a).
 */
/** Clamp the theme's media radius to a sane px integer; anything malformed → 0 (square). */
export function mediaRadiusPx(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return 0;
    return Math.min(80, Math.max(0, Math.round(value)));
}
export function applyThemeVars(t, locale) {
    // Defensive: tolerate a partial / legacy-shaped (lean-5) / malformed theme — SSR must NEVER crash
    // (the render-resilience contract). Merge every field over the v2 Shrine defaults. The real path
    // already ran migrateThemeSettings; this guards direct callers + old-shape fixtures + future drift.
    const d = DEFAULT_THEME_SETTINGS;
    const tt = (t ?? {});
    const c = { ...d.colors, ...tt.colors };
    const ty = (tt.typography ?? {});
    const h = { ...d.typography.heading, ...ty.heading };
    const b = { ...d.typography.body, ...ty.body };
    const shape = { ...d.shape, ...tt.shape };
    const radiusBase = shape.radius === 'sharp'
        ? 'var(--aqx-r-xs)'
        : shape.radius === 'round'
            ? 'var(--aqx-r-full)'
            : 'var(--aqx-r-md)';
    // The seller's family first, then whatever their language actually needs to be legible in.
    // Two layers, in this order: the theme's own face for the script goes IN (after the seller's family,
    // ahead of the generics), then the platform families go ON THE END for the scripts that need them.
    const headingFont = withScriptFallback(withScriptFace(cleanCss(h.font, POPPINS_STACK), locale), locale);
    const bodyFont = withScriptFallback(withScriptFace(cleanCss(b.font, POPPINS_STACK), locale), locale);
    const accentGradient = cleanCss(c.accent2Gradient, 'var(--aqx-theme-accent)');
    const bgGradient = cleanCss(c.background1Gradient, 'var(--aqx-theme-bg)');
    const surfaceGradient = cleanCss(c.background2Gradient, 'var(--aqx-theme-surface)');
    const vars = {
        // ── Colors (base palette; derived tokens color-mix the emitted vars so they track a re-theme) ──
        '--aqx-theme-accent': c.accent1,
        '--aqx-theme-accent-hover': 'color-mix(in srgb, var(--aqx-theme-accent) 82%, black)',
        '--aqx-theme-accent-2': c.accent2,
        '--aqx-theme-accent-2-contrast': accent2Ink(c.accent2, c.solidButtonLabel, c.text),
        '--aqx-theme-accent-gradient': accentGradient,
        '--aqx-theme-accent-contrast': c.solidButtonLabel,
        '--aqx-theme-outline': c.outlineButton,
        '--aqx-theme-ink': c.text,
        '--aqx-theme-muted': 'color-mix(in srgb, var(--aqx-theme-ink) 62%, var(--aqx-theme-bg))',
        '--aqx-theme-subtle': 'color-mix(in srgb, var(--aqx-theme-ink) 49%, var(--aqx-theme-bg))',
        '--aqx-theme-border': 'color-mix(in srgb, var(--aqx-theme-ink) 12%, var(--aqx-theme-bg))',
        '--aqx-theme-bg': c.background1,
        '--aqx-theme-surface': c.background2,
        '--aqx-theme-surface-2': 'color-mix(in srgb, var(--aqx-theme-surface) 96%, black)',
        '--aqx-theme-bg-gradient': bgGradient,
        '--aqx-theme-surface-gradient': surfaceGradient,
        // Fixed semantic tokens — referenceable by a ColorValue token but NOT seller-editable in v1. Emitted
        // here too (same values as theme.css) so every THEME_COLOR_TOKENS member is a var this function sets
        // (the lockstep invariant), and the page root always defines them regardless of stylesheet order.
        '--aqx-theme-success': '#0a8a06',
        '--aqx-theme-star': '#ffcc00',
        // ── Typography ──
        '--aqx-theme-font': bodyFont,
        '--aqx-theme-heading-font': headingFont,
        '--aqx-theme-fw-heading': String(h.weight),
        '--aqx-theme-fw-regular': String(b.weight),
        '--aqx-theme-lh-body': String(b.lineHeight),
        '--aqx-theme-lh-heading': String(h.lineHeight),
        '--aqx-theme-tracking-body': trackingEm(b),
        '--aqx-theme-tracking-heading': trackingEm(h),
        // Font-size scales (0019 Q2/§3a) — numeric, drive the calc()-rebased --aqx-theme-text-* tokens.
        '--aqx-theme-heading-scale': String(h.scale),
        '--aqx-theme-body-scale': String(b.scale),
        // ── Shape / container ──
        '--aqx-theme-container': `${shape.containerWidth}px`,
        // Corner radius of every image/video (0 = square, the default). Read by each media section's CSS;
        // a section's own "Image corners" setting re-declares the var on its root and wins.
        '--aqx-theme-radius-media': `${mediaRadiusPx(shape.mediaRadius)}px`,
        // ── Legacy builder-chrome fallback vars (FallbackSection/ErrorBoundaries/EditableSection read them) ──
        '--aqx-purple': c.accent1,
        '--aqx-bg': c.background1,
        '--aqx-surface': c.background2,
        '--aqx-ink': c.text,
        '--aqx-line': 'color-mix(in srgb, var(--aqx-theme-ink) 12%, var(--aqx-theme-bg))',
        '--aqx-font-sans': bodyFont,
        '--aqx-font-display': headingFont,
        '--aqx-r-base': radiusBase,
        '--aqx-container': `${shape.containerWidth}px`,
        '--aqx-space-scale': String(shape.spacing),
    };
    return vars;
}
//# sourceMappingURL=applyThemeVars.js.map